const WILDCARD_CELL_VALUES = new Set(['', '?', '!', '*']);

function splitCellValueTokens(raw) {
    const text = String(raw ?? '').trim();
    if (!text || WILDCARD_CELL_VALUES.has(text)) return [];
    return text.split(',').map(token => token.trim()).filter(Boolean);
}

function collectUniqueTokens(values) {
    const seen = new Set();
    const tokens = [];
    (values || []).forEach(raw => {
        splitCellValueTokens(raw).forEach(token => {
            const key = token.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            tokens.push(token);
        });
    });
    return tokens;
}

function addTokensToMap(map, header, values) {
    const name = String(header ?? '').trim();
    if (!name) return;
    const existing = map[name] || [];
    map[name] = collectUniqueTokens([...existing, ...(values || [])]);
}

function findUnquotedColon(text) {
    let inQuotes = false;
    let escaped = false;
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (inQuotes) {
            if (escaped) {
                escaped = false;
                continue;
            }
            if (ch === '\\') {
                escaped = true;
                continue;
            }
            if (ch === '"') inQuotes = false;
            continue;
        }
        if (ch === '"') {
            inQuotes = true;
            continue;
        }
        if (ch === ':') return i;
    }
    return -1;
}

function parseAtom(raw) {
    const text = String(raw ?? '').trim();
    if (!text) return { text: '', quoted: false, complete: true };

    if (text.startsWith('"')) {
        let out = '';
        let escaped = false;
        for (let i = 1; i < text.length; i += 1) {
            const ch = text[i];
            if (escaped) {
                out += ch;
                escaped = false;
                continue;
            }
            if (ch === '\\') {
                escaped = true;
                continue;
            }
            if (ch === '"') {
                const rest = text.slice(i + 1).trim();
                return { text: out, quoted: true, complete: rest.length === 0 };
            }
            out += ch;
        }
        return { text: out, quoted: true, complete: false };
    }

    return { text, quoted: false, complete: true };
}

/**
 * Quote a feature or value for the search box, escaping inner quotes.
 */
export function formatSearchTerm(text) {
    const value = String(text ?? '');
    return `"${value.replaceAll('\\', '\\\\').replaceAll('"', '\\"')}"`;
}

/**
 * Parse quick-search syntax:
 * - "Feature":"Value" / Feature:"Value" → featureValue (exact token in that column)
 * - "Feature" → exact (case-insensitive equality)
 * - otherwise → contains (substring)
 */
export function parseSearchQuery(query) {
    const raw = String(query ?? '').trim();
    if (!raw) return { kind: 'empty', text: '' };

    const colonIndex = findUnquotedColon(raw);
    if (colonIndex !== -1) {
        const left = parseAtom(raw.slice(0, colonIndex));
        const right = parseAtom(raw.slice(colonIndex + 1));
        if (left.text && right.text && left.complete && right.complete) {
            return {
                kind: 'featureValue',
                feature: left.text,
                value: right.text,
                exact: true,
                text: raw
            };
        }
    }

    const atom = parseAtom(raw);
    if (atom.quoted && atom.complete && atom.text) {
        return { kind: 'exact', text: atom.text };
    }

    return { kind: 'contains', text: raw };
}

function matchesExact(value, query) {
    return String(value ?? '').trim().toLowerCase() === String(query ?? '').trim().toLowerCase();
}

function matchesContains(value, query) {
    return String(value ?? '').toLowerCase().includes(String(query ?? '').trim().toLowerCase());
}

function matchesText(value, parsed) {
    if (!parsed || parsed.kind === 'empty' || parsed.kind === 'featureValue') return false;
    if (parsed.kind === 'exact') return matchesExact(value, parsed.text);
    return matchesContains(value, parsed.text);
}

function headerKeyIgnoreCase(byHeader, featureName) {
    const wanted = String(featureName ?? '').trim().toLowerCase();
    if (!wanted || !byHeader) return null;
    return Object.keys(byHeader).find(header => header.toLowerCase() === wanted) || null;
}

function headerHasExactValue(byHeader, featureName, value) {
    const key = headerKeyIgnoreCase(byHeader, featureName);
    if (!key) return false;
    const wanted = String(value ?? '').trim().toLowerCase();
    return (byHeader[key] || []).some(token => token.toLowerCase() === wanted);
}

/**
 * Parse feature column headers from a ruleset list item.
 * List DTOs expose headersJson; detail/draft objects expose headers[].
 */
export function parseRuleSetHeaders(ruleSet) {
    if (!ruleSet) return [];

    if (Array.isArray(ruleSet.headers)) {
        return ruleSet.headers.map(h => String(h ?? '')).filter(Boolean);
    }

    const raw = ruleSet.headersJson ?? ruleSet.HeadersJson;
    if (!raw) return [];
    if (Array.isArray(raw)) {
        return raw.map(h => String(h ?? '')).filter(Boolean);
    }

    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.map(h => String(h ?? '')).filter(Boolean);
    } catch {
        return [];
    }
}

/**
 * Distinct non-wildcard cell values from a full ruleset (draft or saved detail).
 */
export function extractCellValuesFromRuleSet(data) {
    if (!data?.rows?.length) return [];
    const rawValues = data.rows.flatMap(row =>
        (row.cells || []).map(cell => cell.expectedValue ?? cell.ExpectedValue)
    );
    return collectUniqueTokens(rawValues);
}

/**
 * Distinct non-wildcard cell tokens grouped by feature/header name.
 */
export function extractCellValuesByHeaderFromRuleSet(data) {
    const map = {};
    if (!data?.rows?.length) return map;

    data.rows.forEach(row => {
        (row.cells || []).forEach(cell => {
            const header = cell.featureName ?? cell.FeatureName;
            addTokensToMap(map, header, [cell.expectedValue ?? cell.ExpectedValue]);
        });
    });

    return map;
}

/**
 * Parse searchable cell values from a ruleset list item.
 * List DTOs expose cellValues; detail/draft objects expose rows[].cells[].
 */
export function parseRuleSetCellValues(ruleSet) {
    if (!ruleSet) return [];

    const listed = ruleSet.cellValues ?? ruleSet.CellValues;
    if (Array.isArray(listed)) {
        return collectUniqueTokens(listed);
    }

    return extractCellValuesFromRuleSet(ruleSet);
}

/**
 * Parse cell tokens grouped by header. Accepts list DTO arrays/maps or draft rows.
 */
export function parseRuleSetCellValuesByHeader(ruleSet) {
    if (!ruleSet) return {};

    const listed = ruleSet.cellValuesByHeader ?? ruleSet.CellValuesByHeader;
    const map = {};

    if (listed && typeof listed === 'object' && !Array.isArray(listed)) {
        Object.entries(listed).forEach(([header, values]) => {
            addTokensToMap(map, header, Array.isArray(values) ? values : [values]);
        });
        return map;
    }

    if (Array.isArray(listed)) {
        listed.forEach(entry => {
            const header = entry?.header ?? entry?.Header;
            const values = entry?.values ?? entry?.Values;
            addTokensToMap(map, header, Array.isArray(values) ? values : [values]);
        });
        return map;
    }

    return extractCellValuesByHeaderFromRuleSet(ruleSet);
}

export function parseRuleSetComments(ruleSet) {
    if (!ruleSet) return [];

    const listed = ruleSet.comments ?? ruleSet.Comments;
    if (Array.isArray(listed)) {
        return listed.map(text => String(text ?? '').trim()).filter(Boolean);
    }

    const notes = ruleSet.notes ?? ruleSet.Notes;
    if (!Array.isArray(notes)) return [];
    return notes
        .map(note => String(note?.text ?? note?.Text ?? '').trim())
        .filter(Boolean);
}

export function isRulesetFilterActive(searchQuery, featureFilters = [], valueFilters = []) {
    return Boolean((searchQuery || '').trim())
        || (featureFilters || []).length > 0
        || (valueFilters || []).length > 0;
}

function matchesValueQuery(cellValues, parsed) {
    if (!parsed || parsed.kind === 'empty' || parsed.kind === 'featureValue') return true;
    if (parsed.kind === 'exact') {
        return cellValues.some(value => matchesExact(value, parsed.text));
    }
    return cellValues.some(value => matchesContains(value, parsed.text));
}

function matchesCommentQuery(comments, parsed) {
    if (!parsed || parsed.kind === 'empty' || parsed.kind === 'featureValue') return true;
    return comments.some(text => matchesText(text, parsed));
}

function uniqueCaseInsensitive(items) {
    const seen = new Set();
    const unique = [];
    (items || []).forEach(item => {
        const text = String(item ?? '').trim();
        if (!text) return;
        const key = text.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        unique.push(text);
    });
    return unique;
}

function matchingValueTokens(cellValues, parsed) {
    if (!parsed || parsed.kind === 'empty' || parsed.kind === 'featureValue') return [];
    return cellValues.filter(value => matchesText(value, parsed));
}

function matchingTokensInHeader(byHeader, featureName, value, exact) {
    const key = headerKeyIgnoreCase(byHeader, featureName);
    if (!key) return [];
    const wanted = String(value ?? '').trim().toLowerCase();
    return (byHeader[key] || []).filter(token => (
        exact ? token.toLowerCase() === wanted : token.toLowerCase().includes(wanted)
    ));
}

/**
 * Explain why a ruleset matched the current filter.
 * Returns [{ type, label, items }] for display next to list results.
 */
export function getRuleSetMatchHits(ruleSet, { mode = 'all', query = '', featureFilters = [], valueFilters = [] } = {}) {
    if (!ruleSet) return [];

    const parsed = parseSearchQuery(query);
    const requiredFeatures = (featureFilters || []).map(f => String(f || '').trim()).filter(Boolean);
    const requiredValues = (valueFilters || []).map(v => String(v || '').trim()).filter(Boolean);

    const name = ruleSet.name || '';
    const headers = parseRuleSetHeaders(ruleSet);
    const cellValues = parseRuleSetCellValues(ruleSet);
    const byHeader = parseRuleSetCellValuesByHeader(ruleSet);
    const comments = parseRuleSetComments(ruleSet);
    const hits = [];

    const includeName = (mode === 'all' || mode === 'name') && parsed.kind !== 'featureValue';
    const includeFeatures = mode === 'all' || mode === 'features' || requiredFeatures.length > 0 || parsed.kind === 'featureValue';
    const includeValues = mode === 'all' || mode === 'values' || requiredValues.length > 0 || parsed.kind === 'featureValue';
    const includeComments = (mode === 'all' || mode === 'comments') && parsed.kind !== 'featureValue';

    if (parsed.kind !== 'empty' && includeName && matchesText(name, parsed)) {
        hits.push({ type: 'name', label: 'Table name', items: [name] });
    }

    if (includeFeatures) {
        const featureItems = [];
        if (requiredFeatures.length > 0) {
            requiredFeatures.forEach(feature => {
                const match = headers.find(h => h.toLowerCase() === feature.toLowerCase());
                if (match) featureItems.push(match);
            });
        }
        if (parsed.kind === 'featureValue') {
            const match = headers.find(h => matchesExact(h, parsed.feature));
            if (match) featureItems.push(match);
        } else if (parsed.kind !== 'empty' && (mode === 'all' || mode === 'features')) {
            headers.filter(h => matchesText(h, parsed)).forEach(h => featureItems.push(h));
        }
        const uniqueFeatures = uniqueCaseInsensitive(featureItems);
        if (uniqueFeatures.length > 0) {
            hits.push({ type: 'features', label: 'Features', items: uniqueFeatures });
        }
    }

    if (includeValues) {
        const valueItems = [];
        if (parsed.kind === 'featureValue') {
            matchingTokensInHeader(byHeader, parsed.feature, parsed.value, true)
                .forEach(token => valueItems.push(token));
        }
        if (requiredValues.length > 0) {
            requiredValues.forEach(value => {
                const scopedFeature = parsed.kind === 'featureValue'
                    ? parsed.feature
                    : (requiredFeatures.length === 1 ? requiredFeatures[0] : null);
                if (scopedFeature) {
                    matchingTokensInHeader(byHeader, scopedFeature, value, true)
                        .forEach(token => valueItems.push(token));
                } else {
                    const valueLower = value.toLowerCase();
                    cellValues
                        .filter(cellValue => cellValue.toLowerCase().includes(valueLower) || cellValue.toLowerCase() === valueLower)
                        .forEach(cellValue => valueItems.push(cellValue));
                }
            });
        }
        if (parsed.kind !== 'empty' && parsed.kind !== 'featureValue' && (mode === 'all' || mode === 'values')) {
            matchingValueTokens(cellValues, parsed).forEach(cellValue => valueItems.push(cellValue));
        }
        const uniqueValues = uniqueCaseInsensitive(valueItems);
        if (uniqueValues.length > 0) {
            hits.push({ type: 'values', label: 'Values', items: uniqueValues });
        }
    }

    if (includeComments && parsed.kind !== 'empty') {
        const commentItems = comments.filter(text => matchesText(text, parsed));
        const uniqueComments = uniqueCaseInsensitive(commentItems);
        if (uniqueComments.length > 0) {
            hits.push({ type: 'comments', label: 'Comments', items: uniqueComments });
        }
    }

    return hits;
}

export function cellMatchesSearchValues(expectedValue, values) {
    if (!values?.length) return false;
    const tokens = splitCellValueTokens(expectedValue).map(token => token.toLowerCase());
    return values.some(value => {
        const q = String(value || '').trim().toLowerCase();
        if (!q) return false;
        return tokens.some(token => token === q);
    });
}

export function buildSearchHighlight(rulesetId, hits = []) {
    const list = Array.isArray(hits) ? hits : [];
    return {
        rulesetId,
        nonce: Date.now(),
        highlightName: list.some(hit => hit.type === 'name'),
        columns: list.find(hit => hit.type === 'features')?.items || [],
        values: list.find(hit => hit.type === 'values')?.items || [],
        comments: list.find(hit => hit.type === 'comments')?.items || []
    };
}

/**
 * Filter rulesets by search mode.
 * - all: query matches table name OR any feature header OR any cell value OR any comment
 * - name: query matches table name only
 * - comments: query matches comment text only
 * - values: query matches cell values only
 * - features: table must contain every selected feature (AND, exact header match)
 * Quoted terms are exact; Feature:Value matches that token in that column.
 * Selected valueFilters also require AND matches against cell values.
 */
export function filterRuleSets(ruleSets, { mode = 'all', query = '', featureFilters = [], valueFilters = [] } = {}) {
    if (!Array.isArray(ruleSets) || ruleSets.length === 0) return [];

    const parsed = parseSearchQuery(query);
    const requiredFeatures = (featureFilters || [])
        .map(f => String(f || '').trim().toLowerCase())
        .filter(Boolean);
    const requiredValues = (valueFilters || [])
        .map(v => String(v || '').trim())
        .filter(Boolean);

    return ruleSets.filter(rs => {
        const name = rs.name || '';
        const headers = parseRuleSetHeaders(rs);
        const headersLower = headers.map(h => h.toLowerCase());
        const cellValues = parseRuleSetCellValues(rs);
        const cellValuesLower = cellValues.map(v => v.toLowerCase());
        const byHeader = parseRuleSetCellValuesByHeader(rs);
        const comments = parseRuleSetComments(rs);

        if (requiredFeatures.length > 0) {
            const hasAllFeatures = requiredFeatures.every(feature => headersLower.includes(feature));
            if (!hasAllFeatures) return false;
        }

        if (parsed.kind === 'featureValue') {
            if (!headersLower.includes(parsed.feature.toLowerCase())) return false;
            if (!headerHasExactValue(byHeader, parsed.feature, parsed.value)) return false;
        }

        if (requiredValues.length > 0) {
            const scopedFeature = parsed.kind === 'featureValue'
                ? parsed.feature
                : (requiredFeatures.length === 1 ? requiredFeatures[0] : null);
            const hasAllValues = requiredValues.every(value => {
                if (scopedFeature) {
                    return headerHasExactValue(byHeader, scopedFeature, value);
                }
                const valueLower = value.toLowerCase();
                return cellValuesLower.some(cellValue => cellValue.includes(valueLower) || cellValue === valueLower);
            });
            if (!hasAllValues) return false;
        }

        if (parsed.kind === 'empty' || parsed.kind === 'featureValue') {
            return true;
        }

        if (mode === 'name') {
            return matchesText(name, parsed);
        }

        if (mode === 'comments') {
            return matchesCommentQuery(comments, parsed);
        }

        if (mode === 'values') {
            return matchesValueQuery(cellValues, parsed);
        }

        if (mode === 'features') {
            return headers.some(header => matchesText(header, parsed));
        }

        return matchesText(name, parsed)
            || headers.some(header => matchesText(header, parsed))
            || matchesValueQuery(cellValues, parsed)
            || matchesCommentQuery(comments, parsed);
    });
}
